import { useEffect, useState } from "react";
import {
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaBuilding,
  FaCheck,
  FaChevronRight,
  FaTimes,
  FaLocationArrow,
  FaFingerprint,
  FaArrowRight,
  FaExchangeAlt,
} from "react-icons/fa";
import { useToast } from "../../../context/ToastContext";
import { useModal } from "../../../context/ConfirmModalContext";
import { useNavigate } from "react-router-dom";
import FullPageSpinner from "../../../components/FullPageSpinner";
import { sendRequest } from "../../../utils/api";
import AttendanceHistory from "../../../models/AttendanceHistory";
import { formatDate, formatDuration, formatTime, getDayName } from "../../../utils/date";
import Site from "../../../models/Site";
import { FaLocationCrosshairs, FaLocationDot, FaRotateRight } from "react-icons/fa6";
import { roundTo } from "../../../utils/helper-function";
import { capitalizeWords } from "../../../utils/string";
import { getUser } from "../../../utils/user-helper";


export default function AttendancePage() {

  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  // const detailsPage = "/attendance/"
  const DP = 6;

  const [loading, setLoading] = useState<boolean>(false);
  const [hasSite, setHasSite] = useState<boolean>(true);

  const [distance, setDistance] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [withinSite, setWithinSite] = useState(false);
  const [hasGoodAccuracy, setHasGoodAccuracy] = useState(false);
  const [hasGpsPermission, setHasGpsPermission] = useState(true);
  const [lat, setLat] = useState<number | null>(null);
  const [lon, setLon] = useState<number | null>(null);

  const [site, setSite] = useState<Site>({} as Site);

  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceHistory[]>([]);
  const [todayAttendance, setTodayAttendance] = (
    useState<AttendanceHistory>({} as AttendanceHistory)
  );

  const [checkedIn, setCheckedIn] = useState(false);
  const [clockIn, setClockIn] = useState<string | null | Date>(null);
  const [clockOut, setClockOut] = useState<string | null | Date>(null);
  const [seconds, setSeconds] = useState(0);

  const [currentTime, setCurrentTime] = useState(new Date());

  const [elapsedTime, setElapsedTime] = useState("00:00:00");

  const [showSiteSwitcher, setShowSiteSwitcher] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [switchingSite, setSwitchingSite] = useState(false);

  const [rosterSchedule, setRosterSchedule] = useState({
    // monday: { shift: "day", branch: "" },
    // tuesday: { shift: "day", branch: "" },
    // wednesday: { shift: "day", branch: "" },
    // thursday: { shift: "day", branch: "" },
    // friday: { shift: "day", branch: "" },
    // saturday: { shift: "day", branch: "" },
    // sunday: { shift: "day", branch: "" },
  });

  useEffect(() => {
    if (!clockIn) return;

    const updateElapsedTime = () => {
      const start = new Date(clockIn).getTime();
      const now = Date.now();

      const diff = Math.max(0, now - start);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(
        `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      );
    };

    updateElapsedTime(); // Update immediately

    const timer = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(timer);
  }, [clockIn]);

  const formattedDate = currentTime.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = currentTime.toLocaleTimeString();

  function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) {

    const R = 6371e3;

    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;

    const Δφ =
      (lat2 - lat1) *
      Math.PI / 180;

    const Δλ =
      (lon2 - lon1) *
      Math.PI / 180;

    const a =
      Math.sin(Δφ / 2) ** 2 +
      Math.cos(φ1) *
      Math.cos(φ2) *
      Math.sin(Δλ / 2) ** 2;

    const c =
      2 *
      Math.atan2(
        Math.sqrt(a),
        Math.sqrt(1 - a)
      );

    return Math.round(R * c);
  }

  const formatAttendanceHistory = (attendance: AttendanceHistory[]) => {
    return (attendance as AttendanceHistory[]).map((attHist, idx) => ({
      ...attHist,
      sn: idx + 1,
      siteName: capitalizeWords(attHist?.site?.name ?? ""),
      siteAddres: attHist?.site?.address,
      clockIn: attHist.clockIn
        ? formatTime(attHist.clockIn) : "---",
      clockOut: attHist.clockOut
        ? formatTime(attHist.clockOut) : "---",
      createdAt: formatDate(attHist.createdAt)
    }))
  };


  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Africa/Lagos",
  });

  const currentAttendance = (attendance: AttendanceHistory[]) => {
    return attendance.find((attendance: AttendanceHistory) => {
      const attendanceDate = new Date(attendance.createdAt).toLocaleDateString(
        "en-CA",
        {
          timeZone: "Africa/Lagos",
        }
      );
      return attendanceDate === today;
    }) as AttendanceHistory;
  }

  // Get Attendance History.
  useEffect(() => {
    const getAttendanceHistory = async () => {
      setLoading(true);
      try {

        const day = getDayName(new Date());

        const response = await sendRequest("GET", "/attendance?take=3");
        const { attendance } = response;

        const todayAttendance = currentAttendance(attendance);

        setCheckedIn(!!todayAttendance?.clockIn);
        setClockIn(todayAttendance?.clockIn);
        setClockOut(todayAttendance?.clockOut);
        setTodayAttendance(todayAttendance);
        setAttendanceHistory(formatAttendanceHistory(attendance));

        const site = await sendRequest("GET", `/sites/current?day=${day}`);
        if (site) setHasSite(true);

        setSite(site);
      } catch (err: any) {
        console.log(err)
        if (err.status === 404)
          setHasSite(false);
      } finally {
        setLoading(false);
      }
    }
    getAttendanceHistory();
  }, [])

  // Calculate User Distance In Real-Time
  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition(

      (position) => {
        const userLatitude = position.coords.latitude;
        const userLongitude = position.coords.longitude;

        setLat(userLatitude);
        setLon(userLongitude);

        const gpsAccuracy = position.coords.accuracy;

        const distance =
          calculateDistance(
            userLatitude,
            userLongitude,
            site?.lat,
            site?.lon
          );
        setDistance(distance);
        setAccuracy(gpsAccuracy);

        const isWithinRadius = distance <= site?.radius
        const hasGoodAccuracy = gpsAccuracy <= 250;

        setWithinSite(isWithinRadius);
        setHasGoodAccuracy(hasGoodAccuracy);
      },

      (error) => {
        // navigate("/location-required")
        // console.log(error);
        setHasGpsPermission(false);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, [site]);

  useEffect(() => {
    let timer: any;

    if (checkedIn) {
      timer = setInterval(() => {
        setSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [checkedIn]);


  if (!attendanceHistory) return null;

  const formatWorkedTime = () => {

    // const createdAt = new Date(todayAttendance?.createdAt).getTime();
    const now = Date.now();
    const createdAt = todayAttendance?.createdAt
      ? new Date(todayAttendance.createdAt).getTime() : now;

    const elapsedSeconds = Math.floor((now - createdAt) / 1000);

    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const secs = elapsedSeconds % 60;

    return `${hours}h ${minutes}m ${secs}s`;
  };

  const handleAttendance = () => {
    if (!lat || !lon || !site || !distance)
      return alert(
        "Unable to verify your location. " +
        "Please check your network connection and GPS, then try again."
      );

    if (!withinSite) {
      const distanceOutside = Math.max(0, Math.round(distance - site.radius));

      return alert(
        "You are outside the allowed clock-in area." +
        `\n\nAllowed radius: ${site.radius}m\n` +
        `Current distance: ${Math.round(distance)}m` +
        `\nYou need to move approximately ${distanceOutside}m ` +
        "closer to the site before you can clock in."
      );
    }

    const title = checkedIn
      ? "Clock Out"
      : "Clock In";

    const msg = checkedIn
      ? "You’re about to clock out and end your attendance session. "
      : "You’re about to clock in for today’s attendance. ";

    return openModal(
      "Confirm " + title,
      msg + "Do you want to continue?",
      async () => {
        setLoading(true);
        try {
          const time = new Date();

          const data = { distance, accuracy };

          if (!checkedIn) {

            setCheckedIn(true);
            setClockIn(time);
            setClockOut(null);
            setSeconds(0);

            const response = await sendRequest(
              "POST", "/attendance", {
              isClockIn: true, siteId: site.id, ...data
            });

            setAttendanceHistory(formatAttendanceHistory([response]));
            setTodayAttendance(response);

            showToast(
              "Clocked in successfully.",
              { type: "success" }
            );

          } else {
            setCheckedIn(false);
            setClockOut(time);

            const response = await sendRequest(
              "POST", "/attendance", {
              isClockIn: false, ...data
            }
            );

            setAttendanceHistory(prev =>
              prev.map(hist =>
                hist.id === todayAttendance.id
                  ? {
                    ...hist,
                    clockOut: formatTime(response.clockOut),
                  }
                  : hist
              )
            );

            showToast(
              "Clocked out successfully.",
              { type: "success" }
            );
          }
        } catch (err: any) {

          if (err.status === 409) {
            return showToast(
              "Today's attendance has already been completed.",
              { type: "error" }
            );
          }
          // showToast(
          //   "Failed to share payslip. Please check your network and try again.",
          //   { type: "error" }
          // );
        } finally {
          setLoading(false);
        }
      }, true, `Yes, ${title}`, "Cancel"
    );
  };


  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log(position);
        navigate("/attendances");
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert(
              "Location permission has been blocked.\n\nPlease click the lock/location icon in your browser's address bar, allow location access for this site, then refresh or retry."
            );
            break;

          case error.POSITION_UNAVAILABLE:
            alert(
              "Unable to determine your location. Please ensure Location Services (GPS) are enabled and try again."
            );
            break;

          case error.TIMEOUT:
            alert(
              "The location request timed out. Please check your GPS and internet connection, then try again."
            );
            break;

          default:
            alert("An unexpected error occurred while retrieving your location.");
        }

        console.error(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const openSiteSwitcher = async () => {
    setShowSiteSwitcher(true);

    try {
      const response = await sendRequest("GET", "/sites");

      setSites(response.sites ?? response);
    } catch (error) {
      console.error(error);
      showToast("Unable to load sites.", { type: "error" });
    }
  };

  const handleSwitchSite = async (selectedSite: Site) => {
    if (selectedSite.id === site.id) {
      setShowSiteSwitcher(false);
      return;
    }

    const day = getDayName(new Date());
    setRosterSchedule({ [day]: { shift: "day", branch: site.id } })

    setSwitchingSite(true);

    const response = await getUser();
    if (!response) return;

    try {
      // If you need to persist the selected site:
      await sendRequest(
        "POST", `/users/${response?.user?.id}/rosters`, { rosterSchedule }
      );

      setSite(selectedSite);
      setShowSiteSwitcher(false);

      showToast(
        `Switched to ${selectedSite.name}.`,
        { type: "success" }
      );
    } catch (error) {
      console.error(error);
      showToast("Unable to switch site.", { type: "error" });
    } finally {
      setSwitchingSite(false);
    }
  };

  if (!hasGpsPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50 p-6">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-indigo-100 flex items-center justify-center">
            {FaLocationDot({ className: "text-5xl text-indigo-600" })}
          </div>

          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            Location Access Required
          </h1>

          <p className="mt-4 text-gray-600 leading-7">
            To use the attendance system, you must enable your device's GPS and
            allow this application to access your current location.
          </p>

          <div className="mt-6 rounded-2xl bg-indigo-50 border border-indigo-100 p-4 text-left">
            <h3 className="font-semibold text-indigo-700 mb-3">
              Why do we need your location?
            </h3>

            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Verify you are at your assigned work site.</li>
              <li>• Prevent unauthorized clock-ins and clock-outs.</li>
              <li>• Ensure accurate attendance records.</li>
            </ul>
          </div>

          <button
            onClick={requestLocation}
            className="mt-8 w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center justify-center gap-2 transition"
          >
            {FaLocationCrosshairs({})}
            Enable Location
          </button>

          <button
            onClick={requestLocation}
            className="mt-3 w-full h-12 rounded-xl border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium flex items-center justify-center gap-2 transition"
          >
            {FaRotateRight({})}
            Retry
          </button>

          <p className="mt-6 text-xs text-gray-500 leading-6">
            If you have previously denied location permission, please enable it in
            your browser settings and refresh the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 space-y-6 font-sans">
      <FullPageSpinner show={loading} />
      {showSiteSwitcher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="
        flex w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-2xl
        max-h-[90vh]
        sm:max-h-[85vh]
      "
            onClick={(e) => e.stopPropagation()}
          >

            {/* HEADER */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50">
                    {FaBuilding({
                      className: "text-indigo-600",
                    })}
                  </div>

                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Switch Site
                    </h2>

                    <p className="text-xs text-gray-500">
                      Select the site you want to use for attendance.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowSiteSwitcher(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              >
                {FaTimes({})}
              </button>
            </div>

            {/* CURRENT SITE */}
            <div className="px-6 pt-5">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Current Site
              </p>

              <div className="flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100">
                  {FaBuilding({
                    className: "text-indigo-600",
                  })}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold capitalize text-gray-900">
                    {site?.name}
                  </p>

                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {site?.address}
                  </p>
                </div>

                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white">
                  {FaCheck({
                    className: "text-[10px]",
                  })}
                </div>
              </div>
            </div>

            {/* SITES */}
            <div className="max-h-[360px] overflow-y-auto px-6 py-5">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                Available Sites
              </p>

              <div className="space-y-2">
                {sites
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => {
                  const isCurrent = item.id === site?.id;

                  return (
                    <>
                      {!isCurrent && <div>
                        <button
                          key={item.id}
                          type="button"
                          disabled={switchingSite}
                          onClick={() => handleSwitchSite(item)}
                          className={`group flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition ${isCurrent
                            ? "border-indigo-200 bg-indigo-50"
                            : "border-gray-100 bg-white hover:border-indigo-200 hover:bg-gray-50"
                            }`}
                        >
                          {/* ICON */}
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isCurrent
                              ? "bg-indigo-100 text-indigo-600"
                              : "bg-gray-100 text-gray-500 group-hover:bg-indigo-50 group-hover:text-indigo-600"
                              }`}
                          >
                            {FaBuilding({})}
                          </div>

                          {/* INFO */}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold capitalize text-gray-900">
                              {item.name}
                            </p>

                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {item.address || "No address provided"}
                            </p>

                            {item.radius && (
                              <p className="mt-1 text-[10px] font-medium text-gray-400">
                                Attendance radius: {item.radius}m
                              </p>
                            )}
                          </div>

                          {/* ACTION */}
                          {isCurrent ? (
                            <span className="rounded-full bg-indigo-600 px-2.5 py-1 text-[10px] font-bold text-white">
                              Current
                            </span>
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full text-gray-300 transition group-hover:bg-indigo-100 group-hover:text-indigo-600">
                              {FaChevronRight({
                                className: "text-xs",
                              })}
                            </div>
                          )}
                        </button>
                      </div>}
                    </>
                  );
                })

                }
              </div>

              {sites.length === 0 && (
                <div className="py-10 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    {FaBuilding({
                      className: "text-gray-400",
                    })}
                  </div>

                  <p className="mt-3 text-sm font-semibold text-gray-700">
                    No sites available
                  </p>

                  <p className="mt-1 text-xs text-gray-400">
                    You don't have access to any other sites.
                  </p>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowSiteSwitcher(false)}
                className="w-full rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-white rounded-3xl shadow-sm border p-7 flex flex-col md:flex-row justify-between">
        <div>
          <h1 className="text-4xl font-black text-gray-900">
            Attendance
          </h1>

          <p className="text-gray-500 mt-2">
            Track attendance, working hours and location.
          </p>
        </div>

        <div className="mt-5 md:mt-0 text-right">
          <p className="text-gray-500">
            {formattedDate}
          </p>

          <p className="text-3xl font-black text-indigo-600">
            {formattedTime}
          </p>
        </div>
      </div>

      {/* HERO */}
      {hasSite ? (

        <div
          className={`rounded-3xl p-8 text-white shadow-xl transition ${clockIn && clockOut
            ? "bg-gradient-to-r from-purple-600 to-violet-700"
            : checkedIn
              ? "bg-gradient-to-r from-green-500 to-emerald-600"
              : "bg-gradient-to-r from-indigo-600 to-blue-600"
            }`}
        >
          <div className="flex flex-col lg:flex-row justify-between gap-10">
            <div>
              <div className="flex items-center gap-4">

                <div className="bg-white/20 p-4 rounded-2xl">
                  {FaCheckCircle({ className: "text-3xl" })}
                </div>

                <div>
                  <p className="text-white/70">
                    Today's Status
                  </p>

                  <h2 className="text-2xl font-black">
                    {clockIn && clockOut
                      ? "Attendance Completed"
                      : checkedIn
                        ? "Checked In"
                        : "Not Checked In"}
                  </h2>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  {FaMapMarkerAlt({
                    className: "mt-1 shrink-0 text-white/90",
                  })}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm text-white/70">
                        Assigned Site
                      </p>

                      <button
                        type="button"
                        onClick={openSiteSwitcher}
                        title="Switch site"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 text-white/80 transition hover:bg-white/20 hover:text-white"
                      >
                        {FaExchangeAlt({
                          className: "text-xs",
                        })}
                      </button>
                    </div>

                    <p className="mt-1 font-bold capitalize text-white">
                      {site?.name}
                    </p>

                    <p className="mt-0.5 text-sm capitalize text-white/80">
                      {site?.address}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-center">
                  {FaLocationArrow({})}
                  <div>

                    <p className="text-white/70">
                      GPS Verification
                    </p>
                    <p className="font-bold">
                      {distance === null
                        ? "Checking location..."
                        : !withinSite
                          ? `Outside Site (${distance}m)`
                          : !hasGoodAccuracy
                            ? `Poor GPS Accuracy (${Math.round(accuracy!)}m)`
                            : `Within Site (${distance}m)`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleAttendance}
              disabled={Boolean(clockIn && clockOut)}
              className={`px-12 py-5 rounded-2xl font-black text-lg shadow-xl transition ${Boolean(clockIn && clockOut)
                ? "bg-gray-400 text-gray-200 cursor-not-allowed shadow-none opacity-70"
                : checkedIn
                  ? "bg-red-500 hover:bg-red-600 hover:-translate-y-1 active:scale-95"
                  : "bg-white text-indigo-700 hover:bg-gray-100 hover:-translate-y-1 active:scale-95"
                }`}
            >
              {FaFingerprint({ className: "inline mr-2" })}

              {Boolean(clockIn && clockOut)
                ? "COMPLETED"
                : checkedIn
                  ? "CLOCK OUT"
                  : "CLOCK IN"}
            </button>

          </div>
        </div>

      ) : (

        <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-3xl p-8 text-white shadow-xl">

          <div className="flex flex-col lg:flex-row justify-between gap-10">

            <div>

              <div className="flex items-center gap-4">

                <div className="bg-white/20 p-4 rounded-2xl">
                  🚧
                </div>

                <div>

                  <p className="text-white/70">
                    Today's Status
                  </p>

                  <h2 className="text-2xl font-black">
                    No Site Assigned
                  </h2>

                </div>

              </div>

              <div className="mt-8 max-w-2xl">

                <p className="text-white/90 leading-7">
                  You don't have an assigned work site for today, so attendance cannot be recorded.
                  Please contact your supervisor or HR if you believe this is a mistake.
                </p>

              </div>

            </div>

            <button
              disabled
              className="px-12 py-5 rounded-2xl bg-white/20 border border-white/20 text-white font-black cursor-not-allowed opacity-80"
            >
              NO SITE ASSIGNED
            </button>

          </div>

        </div>

      )}

      {/* STATS */}
      {hasSite && <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">

        {
          [
            ["Clock In", clockIn ? formatTime(clockIn) : "--"],
            ["Clock Out", clockOut ? formatTime(clockOut) : "--"],
            ["Working Time", clockIn && clockOut
              ? formatDuration(clockIn, clockOut)
              : formatWorkedTime()
            ],
            ["Distance", distance ? `${roundTo(distance, DP)}m` : null]
          ].map(item => (

            <div
              key={item[0]}
              className="bg-white rounded-3xl shadow-sm border p-6 hover:shadow-md transition"
            >
              <div className="flex gap-3 text-indigo-600">
                {FaClock({})}

                <p className="text-gray-500">
                  {item[0]}
                </p>
              </div>
              <h2 className="text-3xl font-black mt-5">
                {item[1] ? (
                  item[1]
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
                )}
              </h2>
            </div>
          ))
        }
      </div>}

      {/* LOCATION */}
      {hasSite && <div className="bg-white rounded-3xl shadow-sm border p-6">
        <h2 className="text-xl font-bold mb-5">
          Location Verification
        </h2>

        <div className="grid md:grid-cols-4 gap-5">
          {
            [
              ["Latitude", lat ? roundTo(lat, DP) : lat],
              ["Longitude", lon ? roundTo(lon, DP) : lon],
              ["Accuracy", accuracy ? `${roundTo(accuracy, DP)}m` : null],
              ["Allowed Radius", site?.radius ? `${roundTo(site?.radius, DP)}m` : null]
            ].map(item => (

              <div
                key={item[0]}
                className="border rounded-xl p-5"
              >
                <p className="text-gray-500">
                  {item[0]}
                </p>
                <h3 className="font-bold mt-2 flex items-center">
                  {item[1] ? (
                    item[1]
                  ) : (
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></div>
                  )}
                </h3>
              </div>
            ))
          }
        </div>
      </div>}

      {/* HISTORY */}
      {attendanceHistory.length > 0 && <>
        {/* Recent Attendance */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Attendance
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                Your latest attendance records.
              </p>
            </div>

            <button
              className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
              onClick={() => navigate("/attendance/history")}
            >
              View All
              {FaArrowRight({ size: 11 })}
            </button>
          </div>

          {/* Desktop */}
          {/* Desktop */}
          <div className="mt-5 hidden overflow-x-auto md:block">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <th className="px-3 py-3">
                    S/N
                  </th>

                  <th className="px-3 py-3">
                    Date
                  </th>

                  <th className="px-3 py-3">
                    Clock In
                  </th>

                  <th className="px-3 py-3">
                    Clock Out
                  </th>

                  <th className="px-3 py-3">
                    Site
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {attendanceHistory.map((item, idx: number) => (
                  <tr key={item.id}>

                    <td className="px-3 py-4 text-sm font-medium text-gray-800">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-4 text-sm font-medium text-gray-800">
                      {formatDate(item.createdAt)}
                    </td>

                    <td className="px-3 py-4 text-sm text-gray-600">
                      {item.clockIn}
                    </td>

                    <td className="px-3 py-4 text-sm text-gray-600">
                      {item.clockOut}
                    </td>

                    <td className="px-3 py-4 text-sm text-gray-600 capitalize">
                      {item?.site?.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="mt-5 space-y-3 md:hidden">
            {attendanceHistory.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-gray-100 p-4"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800">
                    {item.createdAt}
                  </p>

                  <span
                    className="text-sm font-semibold text-gray-800 capitalize"
                  >
                    {item.site?.name}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">
                      Clock In
                    </p>

                    <p className="mt-1 text-sm font-medium text-gray-700">
                      {item.clockIn}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400">
                      Clock Out
                    </p>

                    <p className="mt-1 text-sm font-medium text-gray-700">
                      {item.clockOut}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
      }
    </div>
  );
}